import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Permission } from '../enums/permission.enum';
import { RbacService } from '../services/rbac.service';

type CanMode = 'all' | 'any';
type CanPermissions = Permission | readonly Permission[];

@Directive({
  selector: '[appCan]',
  standalone: true,
})
export class CanDirective {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly rbac = inject(RbacService);
  private readonly permissions = signal<CanPermissions | null>(null);
  private readonly mode = signal<CanMode>('all');
  private hasView = false;

  @Input()
  set appCan(permission: CanPermissions | null | undefined) {
    this.permissions.set(permission ?? null);
  }

  @Input()
  set appCanMode(mode: CanMode) {
    this.mode.set(mode);
  }

  constructor() {
    effect(() => {
      this.syncView(this.isAllowed());
    });
  }

  private isAllowed(): boolean {
    const permissions = this.permissions();

    if (!permissions) {
      return false;
    }

    if (isPermissionList(permissions)) {
      return this.mode() === 'any'
        ? this.rbac.canAny(permissions)
        : this.rbac.canAll(permissions);
    }

    return this.rbac.can(permissions);
  }

  private syncView(isAllowed: boolean): void {
    if (isAllowed && !this.hasView) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
      this.hasView = true;

      return;
    }

    if (!isAllowed && this.hasView) {
      this.viewContainerRef.clear();
      this.hasView = false;
    }
  }
}

function isPermissionList(permissions: CanPermissions): permissions is readonly Permission[] {
  return Array.isArray(permissions);
}
